<script>
import ApplicantHeader from "@/components/header/applicant-header.vue";

// Layout for the applicant portal (dashboard, notifications, application log).
// Kept separate from layouts/main.vue so the staff and admin screens are untouched.
export default {
  name: "ApplicantLayout",
  components: { ApplicantHeader },
  data() {
    return { year: new Date().getFullYear() };
  },
};
</script>

<template>
  <div class="ecu-shell">
    <slot name="header">
      <ApplicantHeader />
    </slot>

    <main class="ecu-main">
      <div class="ecu-container">
        <slot />
      </div>
    </main>

    <footer class="ecu-footer">
      <div class="ecu-footer__inner">
        <span>{{ year }} @ Eastern Credit union.</span>
      </div>
    </footer>
  </div>
</template>

<!--
  Not scoped on purpose: these are the shared building blocks (page heading, card,
  tile, chip, skeleton, ...) used by the applicant pages. Everything is namespaced
  under .ecu-shell so nothing leaks into the staff/admin screens.
-->
<style>
.ecu-shell {
  --ecu-orange: #f97316;
  --ecu-orange-dark: #e84d00;
  --ecu-orange-soft: #fff1e6;
  --ecu-navy: #0b1f4b;
  --ecu-ink: #1d2a4d;
  --ecu-muted: #5f6579;
  --ecu-border: #eceef5;
  --ecu-footer: #0a0a0a;

  position: relative;
  isolation: isolate;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: var(--ecu-ink);
  font-size: var(--ecu-fs-body);
  font-family: "Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, -apple-system,
    sans-serif;
  background: #fdfdff;
}

/* soft orange glow from the left + soft blue glow from the right, kept very subtle */
.ecu-shell::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(720px 496px at -6% 18%, rgba(249, 115, 22, 0.11), transparent 62%),
    radial-gradient(656px 480px at 106% 42%, rgba(59, 130, 246, 0.11), transparent 62%),
    radial-gradient(480px 304px at 50% -10%, rgba(249, 115, 22, 0.04), transparent 70%),
    linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
}

.ecu-main {
  flex: 1;
  animation: ecu-rise 0.4s ease both;
}
.ecu-container {
  max-width: 1320px;
  margin: 0 auto;
  padding: 20.2px 19.2px 30.2px;
}

.ecu-footer {
  background: var(--ecu-footer);
  color: rgba(255, 255, 255, 0.92);
  font-size: var(--ecu-fs-small);
}
.ecu-footer__inner {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 19.2px;
  height: 51.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9.6px;
}
.ecu-footer__inner span {
    font-size: 13px;
    font-weight: 600;
}
/* ---------- page heading ---------- */
.ecu-shell .ecu-page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12.8px;
  flex-wrap: wrap;
  margin-bottom: 22.4px;
}
.ecu-shell .ecu-page-head__bar {
  display: block;
  width: 41.6px;
  height: 4px;
  border-radius: 79.2px;
  background: linear-gradient(90deg, #ff7a1a, #ffa45c);
  margin-bottom: 12.8px;
}
.ecu-shell .ecu-page-head h1 {
  margin: 0;
  font-size: var(--ecu-fs-h1);
  line-height: 1.1;
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.02em;
  color: var(--ecu-navy);
}
.ecu-shell .ecu-page-head__sub {
  margin: 6.4px 0 0;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-muted);
}

/* ---------- card ---------- */
.ecu-shell .ecu-card {
  background: #fff;
  border: 1px solid rgba(11, 31, 75, 0.04);
  border-radius: 19.2px;
  padding: 20.8px;
  box-shadow: 0 11.2px 35.2px rgba(20, 40, 90, 0.07);
}
.ecu-shell .ecu-card__head {
  display: flex;
  align-items: center;
  gap: 12.8px;
  margin-bottom: 17.6px;
}
.ecu-shell .ecu-card__title {
  margin: 0;
  font-size: var(--ecu-fs-h2);
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.01em;
  color: var(--ecu-navy);
  word-break: break-word;
}

/* ---------- icon tile ---------- */
.ecu-shell .ecu-tile {
  width: 35.2px;
  height: 35.2px;
  border-radius: 11.2px;
  background: var(--ecu-orange-soft);
  color: var(--ecu-orange);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ecu-shell .ecu-tile--lg {
  width: 46.4px;
  height: 46.4px;
  border-radius: 14.4px;
}

/* ---------- buttons ---------- */
.ecu-shell .ecu-btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6.4px;
  padding: 8px 16px;
  border: 1.5px solid var(--ecu-orange);
  border-radius: 9.6px;
  background: #fff;
  color: var(--ecu-orange-dark);
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-btn);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
.ecu-shell .ecu-btn-outline:hover {
  background: var(--ecu-orange);
  color: #fff;
  transform: translateY(-1px);
}
.ecu-shell .ecu-btn-solid {
  display: inline-flex;
  align-items: center;
  gap: 6.4px;
  padding: 8.8px 17.6px;
  border: 0;
  border-radius: 9.6px;
  background: linear-gradient(135deg, #ff7a1a, #f2610f);
  color: #fff;
  font-size: var(--ecu-fs-body);
  font-weight: var(--ecu-fw-btn);
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 6.4px 16px rgba(242, 97, 15, 0.25);
}
.ecu-shell .ecu-btn-solid:hover {
  color: #fff;
}

/* ---------- status chip ---------- */
.ecu-shell .ecu-chip {
  display: inline-flex;
  align-items: center;
  gap: 4.8px;
  padding: 4.8px 11.2px;
  border-radius: 799.2px;
  font-size: var(--ecu-fs-small);
  font-weight: 700;
  white-space: nowrap;
  background: #eef0f6;
  color: #4b5675;
}
.ecu-shell .ecu-chip--success { background: #e6f6ec; color: #0f8a3c; }
.ecu-shell .ecu-chip--warning { background: #fff3dc; color: #b86e00; }
.ecu-shell .ecu-chip--info    { background: #e6f0ff; color: #1d5fd1; }
.ecu-shell .ecu-chip--danger  { background: #fde9e9; color: #c52222; }
.ecu-shell .ecu-chip--orange  { background: var(--ecu-orange-soft); color: var(--ecu-orange-dark); }

/* ---------- loading skeleton ---------- */
.ecu-shell .ecu-skeleton {
  border-radius: 8px;
  background: linear-gradient(90deg, #f1f2f8 25%, #f8f9fc 50%, #f1f2f8 75%);
  background-size: 200% 100%;
  animation: ecu-shimmer 1.3s linear infinite;
}

/* ---------- empty / error state ---------- */
.ecu-shell .ecu-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 35.2px 16px;
  color: var(--ecu-muted);
}
.ecu-shell .ecu-state h3 {
  margin: 4.8px 0 0;
  font-size: var(--ecu-fs-h3);
  font-weight: var(--ecu-fw-subheading);
  color: var(--ecu-navy);
}
.ecu-shell .ecu-state p {
  margin: 0;
  max-width: 304px;
  font-size: var(--ecu-fs-body);
}
.ecu-shell .ecu-state__icon {
  width: 57.6px;
  height: 57.6px;
  border-radius: 17.6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ecu-orange-soft);
  color: var(--ecu-orange);
}
.ecu-shell .ecu-state__icon--danger {
  background: #fde9e9;
  color: #c52222;
}

@keyframes ecu-shimmer {
  to { background-position: -200% 0; }
}
@keyframes ecu-rise {
  from { opacity: 0; transform: translateY(6.4px); }
  to   { opacity: 1; transform: none; }
}

@media (max-width: 767px) {
  .ecu-container { padding: 20.2px 19.2px 30.2px; }
  .ecu-shell .ecu-page-head h1 { font-size: var(--ecu-fs-h1-sm); }
  .ecu-shell .ecu-card { padding: 16px; border-radius: 16px; }
  .ecu-footer__inner { flex-direction: column; justify-content: center; height: auto; padding: 11.2px 12.8px; gap: 3.2px; font-size: var(--ecu-fs-small); }
}
@media (prefers-reduced-motion: reduce) {
  .ecu-main, .ecu-shell .ecu-skeleton { animation: none; }
}
</style>